function firstOf(...candidates) {
  const v = candidates.find((c) => c != null && String(c).trim() !== "");
  return v != null ? String(v).trim() : "";
}

function formatPrimaryPhone(user) {
  const code = user?.primaryPhoneCountryCode ?? user?.PrimaryPhoneCountryCode ?? "";
  const num = user?.primaryPhone ?? user?.PrimaryPhone ?? "";
  if (!num) return "";
  return (code ? `+${String(code).replace(/^\++/, "")} ` : "") + num;
}

/**
 * Resolves pharmacy fields from user (PharmacyContactPerson + pharmacy/store) and optional pharmacyStores.
 * Use with currentLoggedInUserDetail and pharmacyStores from Redux (state.currentLoggedInUserDetail / state.users).
 */
export function getPharmacyInfo(user, pharmacyStores = []) {
  if (!user) return null;
  const pharmacy = user.pharmacy ?? user.Pharmacy ?? {};
  const store = user.store ?? user.Store ?? {};

  const phone =
    formatPrimaryPhone(user) ||
    firstOf(user.phone, user.phoneNumber, pharmacy.primaryPhone, store.phone, store.phoneNumber);
  const fax = firstOf(user.fax, user.Fax, pharmacy.fax, store.fax);
  const email = firstOf(user.email, user.Email, pharmacy.email, store.email);
  const pharmacyName = firstOf(pharmacy.name, user.pharmacyName, pharmacy.pharmacyName, user.organizationName);
  const storeNum =
    firstOf(store.storeNumber, store.storeName, user.storeNumber, user.storeName) ||
    (user.storeId ? String(user.storeId) : "");
  const storeLabel = storeNum ? `Store # ${storeNum}` : "";
  const address = firstOf(store.storeAddress, store.address, pharmacy.pharmacyAddress, pharmacy.address, user.pharmacyAddress);

  const base = {
    pharmacyName,
    pharmacyStoreNumber: storeLabel,
    pharmacyPhone: phone,
    pharmacyFax: fax,
    pharmacyEmail: email,
    pharmacyAddress: address,
  };
  const storeId = user.storeId ?? user.selectedStoreId;
  const list = Array.isArray(pharmacyStores) ? pharmacyStores : [];

  if (storeId && list.length > 0) {
    for (const ph of list) {
      const st = ph.stores?.find((s) => String(s.storeId) === String(storeId));
      if (st) {
        const sn = st.storeNumber || st.storeName || storeId;
        const addr = firstOf(st.storeAddress, st.address, ph.pharmacyAddress, ph.address) || address;
        return {
          pharmacyName: firstOf(ph.name, ph.pharmacyName) || pharmacyName,
          pharmacyStoreNumber: `Store # ${sn}`,
          pharmacyPhone: firstOf(st.phone, st.phoneNumber, ph.phone, ph.phoneNumber) || phone,
          pharmacyFax: firstOf(st.fax, ph.fax) || fax,
          pharmacyEmail: firstOf(st.email, st.emailAddress, ph.email, ph.emailAddress) || email,
          pharmacyAddress: addr,
        };
      }
    }
  }
  return base;
}

/**
 * Resolves pharmacist display fields from the logged-in user.
 * Use with currentLoggedInUserDetail from Redux.
 */
export function getPharmacistInfo(user) {
  if (!user) return null;
  const getFullName = (u) => (u?.firstName && u?.lastName ? `${u.firstName} ${u.lastName}` : null);
  const getSignatureUrl = (u) => {
    if (!u?.signature || typeof u.signature !== "string" || u.signature.length === 0) return null;
    return u.signature.startsWith("data:") ? u.signature : `data:image/png;base64,${u.signature}`;
  };
  const getLicenseNumber = (u) => {
    if (!u?.driverLicence || typeof u.driverLicence !== "string" || u.driverLicence.trim().length === 0) return "";
    return u.driverLicence.trim();
  };
  return {
    pharmacistName: getFullName(user) ?? "-",
    pharmacistSignatureUrl: getSignatureUrl(user),
    pharmacistLicenseNumber: getLicenseNumber(user) || "",
  };
}
